import { get } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import type {
  ApiResponse,
  CCApiResponse,
  ILocationGroup,
  IScheduleDaypart,
  IWeekdayDaypart,
} from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

export const mapScheduleDayparts = (
  response: CCApiResponse,
  weekdayDayparts: IWeekdayDaypart[],
): IScheduleDaypart[] => {
  const locationGroups = get(response, 'includes.locationGroups.data', []);

  return response.data.map((item: ApiResponse) =>
    mapScheduleDaypart(item, weekdayDayparts, locationGroups),
  );
};

export const mapScheduleDaypart = (
  response: ApiResponse,
  weekdayDayparts: IWeekdayDaypart[],
  locationGroups: ILocationGroup[],
) => {
  return {
    ...response,
    weekdayDaypart: weekdayDayparts.find(
      (item) => item.id === response.weekday_daypart_id,
    ),
    locationGroup: locationGroups.find(
      (item) => item.id === response.location_group_id,
    ),
  } as IScheduleDaypart;
};

export const createMissingScheduleDaypartsForLocationGroup = async (
  locationGroupId: number,
) => {
  const [weekdayDaypartsResponse, scheduleDaypartsResponse] = await Promise.all(
    [
      queryClient.fetchQuery({
        queryKey: queryKeys.weekdayDayparts.all(['pageSize=0']),
        queryFn: () =>
          requestCCApi({
            url: `/weekdayDayparts${generateQuery([], ['pageSize=0'])}`,
          }) as Promise<{ data: IWeekdayDaypart[] }>,
      }),
      queryClient.fetchQuery({
        queryKey: queryKeys.scheduleDayparts.all([
          'pageSize=0',
          `location_group_id=${locationGroupId}`,
        ]),
        queryFn: () =>
          requestCCApi({
            url: `/scheduleDayparts${generateQuery(
              [],
              ['pageSize=0', `location_group_id=${locationGroupId}`],
            )}`,
          }) as Promise<{ data: IScheduleDaypart[] }>,
      }),
    ],
  );

  const weekdayDayparts = weekdayDaypartsResponse.data;
  const scheduleDayparts = scheduleDaypartsResponse.data;

  const existingWeekdayDaypartIds = new Set(
    scheduleDayparts.map((daypart) => daypart.weekday_daypart_id),
  );

  const missingWeekdayDaypartIds = weekdayDayparts
    .filter(
      (weekdayDaypart) => !existingWeekdayDaypartIds.has(weekdayDaypart.id),
    )
    .map((weekdayDaypart) => weekdayDaypart.id);

  if (missingWeekdayDaypartIds.length === 0) return;

  const creationPayload = missingWeekdayDaypartIds.map((weekdayDaypart) => ({
    resource: 'scheduleDayparts',
    method: 'POST',
    data: {
      weekday_daypart_id: weekdayDaypart,
      location_group_id: locationGroupId,
      start_time: null,
    },
  }));

  await requestCCApi({
    url: '/transaction',
    method: 'POST',
    data: {
      data: creationPayload,
    },
  });

  await queryClient.invalidateQueries(
    queryKeys.scheduleDayparts.invalidateAll(),
  );
};
